import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Eye, EyeOff, Mail, Lock, User, Building, ArrowLeft } from "lucide-react";

import { API_URL } from "@/utils/api";

export default function RegisterPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: "", email: "", password: "", company_name: "", entity_type: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [redirectTo, setRedirectTo] = useState(null);

  // Handle redirect via useEffect to work better with iOS
  useEffect(() => {
    if (redirectTo) {
      const timer = setTimeout(() => {
        window.location.href = redirectTo;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [redirectTo]);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || "Registration failed");
      }
      
      // Check if verification is required
      if (data.requires_verification) {
        toast.success("Account created! Please check your email to verify.");
        setRedirectTo(`/verify-email-pending?email=${encodeURIComponent(formData.email)}`);
      } else {
        localStorage.setItem("auth_token", data.access_token);
        toast.success("Account created successfully!");
        setRedirectTo("/dashboard");
      }
    } catch (err) {
      toast.error(err.message);
      setLoading(false);
    }
  };

  // Show redirecting state
  if (redirectTo) {
    return (
      <div className="min-h-screen flex items-center justify-center font-['Inter'] bg-white">
        <div className="text-center px-8">
          <div className="w-16 h-16 border-4 border-[#BFFF00] border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <h2 className="text-xl font-semibold text-[#121212] mb-2">Redirecting...</h2>
          <p className="text-[#121212]/60 mb-4">Setting up your account</p>
          <a 
            href={redirectTo}
            className="text-[#121212] underline font-medium"
          >
            Click here if not redirected
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex font-['Inter']">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex flex-1 items-center justify-center p-12 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #F7FFE5 0%, #EDFFC2 100%)' }}>
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: 'linear-gradient(#BFFF00 1px, transparent 1px), linear-gradient(90deg, #BFFF00 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        
        <div className="relative max-w-lg text-center">
          <div className="w-24 h-24 bg-[#BFFF00] rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-[#BFFF00]/30">
            <span className="text-5xl font-extrabold text-[#121212] font-['Manrope']">F</span>
          </div>
          <h2 className="text-4xl font-bold text-[#121212] mb-4 font-['Manrope']">Start Your Financial Journey</h2>
          <p className="text-[#121212]/60 text-lg leading-relaxed mb-8">Join thousands of creators and founders who trust FinAI for their bookkeeping needs.</p>
          
          {/* Stats Badges */}
          <div className="flex flex-wrap justify-center gap-4">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-5 py-3 border-2 border-dashed border-[#BFFF00]/50">
              <span className="font-bold text-[#121212]">100+</span>
              <span className="text-[#121212]/60 ml-1">Years Exp</span>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-5 py-3 border-2 border-dashed border-[#BFFF00]/50">
              <span className="font-bold text-[#121212]">$5K+</span>
              <span className="text-[#121212]/60 ml-1">Avg Savings</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 lg:px-24 overflow-y-auto py-12 bg-white">
        <div className="max-w-md w-full mx-auto">
          <Link to="/" className="inline-flex items-center text-[#121212]/60 hover:text-[#121212] mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to home
          </Link>
          
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-[#121212] mb-2 font-['Manrope']">Create your account</h1>
            <p className="text-[#121212]/60">Get started with professional bookkeeping</p>
          </div>

          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-[#121212]/70 font-medium">Full Name</Label>
              <div className="relative mt-2">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#121212]/40" />
                <Input 
                  id="name" 
                  value={formData.name} 
                  onChange={(e) => handleChange("name", e.target.value)} 
                  placeholder="John Doe" 
                  className="pl-12 h-14 rounded-2xl bg-[#F7FFE5] border-2 border-[#BFFF00]/30 focus:border-[#BFFF00] focus:ring-2 focus:ring-[#BFFF00]/20" 
                  data-testid="register-name-input" 
                  required 
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="email" className="text-[#121212]/70 font-medium">Email</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#121212]/40" />
                <Input 
                  id="email" 
                  type="email" 
                  value={formData.email} 
                  onChange={(e) => handleChange("email", e.target.value)} 
                  placeholder="you@company.com" 
                  className="pl-12 h-14 rounded-2xl bg-[#F7FFE5] border-2 border-[#BFFF00]/30 focus:border-[#BFFF00] focus:ring-2 focus:ring-[#BFFF00]/20" 
                  data-testid="register-email-input" 
                  required 
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="password" className="text-[#121212]/70 font-medium">Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#121212]/40" />
                <Input 
                  id="password" 
                  type={showPassword ? "text" : "password"} 
                  value={formData.password} 
                  onChange={(e) => handleChange("password", e.target.value)} 
                  placeholder="Create a strong password" 
                  className="pl-12 pr-12 h-14 rounded-2xl bg-[#F7FFE5] border-2 border-[#BFFF00]/30 focus:border-[#BFFF00] focus:ring-2 focus:ring-[#BFFF00]/20" 
                  data-testid="register-password-input" 
                  required 
                />
                <button 
                  type="button" 
                  onClick={() => setShowPassword(!showPassword)} 
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#121212]/40 hover:text-[#121212]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <div>
              <Label htmlFor="company" className="text-[#121212]/70 font-medium">Company Name <span className="text-[#121212]/40">(Optional)</span></Label>
              <div className="relative mt-2">
                <Building className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#121212]/40" />
                <Input 
                  id="company" 
                  value={formData.company_name} 
                  onChange={(e) => handleChange("company_name", e.target.value)} 
                  placeholder="Your Company LLC" 
                  className="pl-12 h-14 rounded-2xl bg-[#F7FFE5] border-2 border-[#BFFF00]/30 focus:border-[#BFFF00] focus:ring-2 focus:ring-[#BFFF00]/20" 
                  data-testid="register-company-input" 
                />
              </div>
            </div>
            
            <div>
              <Label className="text-[#121212]/70 font-medium">Entity Type <span className="text-[#121212]/40">(Optional)</span></Label>
              <Select value={formData.entity_type} onValueChange={(v) => handleChange("entity_type", v)}>
                <SelectTrigger className="h-14 rounded-2xl mt-2 bg-[#F7FFE5] border-2 border-[#BFFF00]/30 focus:border-[#BFFF00]" data-testid="register-entity-select">
                  <SelectValue placeholder="Select entity type" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="individual">Individual</SelectItem>
                  <SelectItem value="partnership">Partnership Firm</SelectItem>
                  <SelectItem value="llc">LLC</SelectItem>
                  <SelectItem value="llp">LLP</SelectItem>
                  <SelectItem value="scorp">S-Corporation</SelectItem>
                  <SelectItem value="ccorp">C-Corporation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button 
              type="submit" 
              disabled={loading} 
              data-testid="register-submit-btn" 
              className="w-full h-14 rounded-full bg-[#121212] hover:bg-[#2a2a2a] text-white font-semibold text-base mt-6 transition-all hover:scale-[1.02]"
            >
              {loading ? "Creating account..." : "Create account"}
            </Button>
          </form>
          
          <p className="mt-8 text-center text-[#121212]/60">
            Already have an account?{" "}
            <Link to="/login" className="text-[#121212] hover:text-[#121212]/80 font-semibold">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
